import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Coins,
  Trophy,
  BadgeCheck,
  Megaphone,
  Users,
  Heart,
  ShieldCheck,
  ArrowRight,
  ChevronLeft,
  Loader2,
  Clock,
  XCircle,
  Instagram,
  Youtube,
  LayoutDashboard,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AmbassadorBadge from "@/components/AmbassadorBadge";

/**
 * AmbassadorApply — public program page that explains what a Panda
 * Ambassador is, the perks, the requirements, and lets the user apply
 * inline. If not signed in, the apply CTA redirects to login with a
 * `next` param so they land back here after auth.
 *
 * Route: /ambassadors/apply (public — same posture as /ambassadors)
 */
const PERKS = [
  {
    icon: BadgeCheck,
    title: "Golden badge on your profile",
    body: "A verified Ambassador chip everyone can see across the feed, live rooms, and the leaderboard.",
  },
  {
    icon: Coins,
    title: "500 coins every month",
    body: "Automatic monthly drop straight into your wallet — no strings, no cash out required.",
  },
  {
    icon: Heart,
    title: "Referral commissions in real USD",
    body: "Earn a slice of every tip & shop sale from creators you referred — rates step down over their first months. Paid via Stripe.",
  },
  {
    icon: Trophy,
    title: "Tier promotions as you grow",
    body: "Bronze at approval, Silver at 20 referrals, Gold at 50. Higher tier = brighter pin, bigger boosts.",
  },
  {
    icon: Megaphone,
    title: "Boosted visibility",
    body: "Priority placement in Discover, Live Now, and Social Meet so more people find you.",
  },
];

const STEPS = [
  {
    n: 1,
    title: "Create your Panda X account",
    body: "You need an active profile with an avatar, a display name, and a completed age gate.",
  },
  {
    n: 2,
    title: "Tell us your pitch",
    body: "In 20 – 1000 characters, share why you'd be a great Ambassador. Focus on your community, your vibe, and what you'd bring to the app.",
  },
  {
    n: 3,
    title: "We review within a few days",
    body: "Our team checks pitches manually. You'll get a notification the moment your application is approved.",
  },
  {
    n: 4,
    title: "Start earning + inviting",
    body: "Once you're in, your badge appears instantly, coin drops begin, and every friend you refer bumps your tier.",
  },
];

const REQUIREMENTS = [
  "18+ verified through the age gate",
  "Active for at least 7 days on Panda X",
  "Zero standing content violations or bans",
  "A genuine intent to grow the community — no bots, no farming",
];

export default function AmbassadorApply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // null = still checking, false = anon, object = signed in.
  const authChecking = user === null;
  const isLoggedIn = !!user && user !== false;

  const [state, setState] = useState(null); // ambassador status from /ambassador/me
  const [loadingState, setLoadingState] = useState(false);
  const [pitch, setPitch] = useState("");
  const [socials, setSocials] = useState({ instagram: "", tiktok: "", x: "", youtube: "" });
  const [followerCount, setFollowerCount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load ambassador state only when signed in.
  useEffect(() => {
    if (authChecking) return;
    if (!isLoggedIn) {
      setLoadingState(false);
      return;
    }
    let cancelled = false;
    setLoadingState(true);
    api
      .get("/ambassador/me")
      .then(({ data }) => {
        if (cancelled) return;
        setState(data);
        if (data.pitch) setPitch(data.pitch);
        if (data.socials) {
          setSocials({
            instagram: data.socials.instagram || "",
            tiktok:    data.socials.tiktok    || "",
            x:         data.socials.x         || "",
            youtube:   data.socials.youtube   || "",
          });
        }
        if (data.follower_count) setFollowerCount(String(data.follower_count));
      })
      .catch(() => {
        if (!cancelled) setState({ status: "none", tier: null, monthly_drop_coins: 500 });
      })
      .finally(() => {
        if (!cancelled) setLoadingState(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, authChecking]);

  const status = state?.status || "none";
  const monthlyCoins = state?.monthly_drop_coins || 500;
  const pitchTooShort = pitch.trim().length < 20;

  const primaryCta = useMemo(() => {
    if (!isLoggedIn) {
      return {
        label: "Sign in to apply",
        onClick: () => navigate(`/login?next=${encodeURIComponent("/ambassadors/apply")}`),
        testid: "ambassador-apply-signin-cta",
        disabled: false,
      };
    }
    if (status === "approved") {
      return {
        label: "Open my Ambassador dashboard",
        onClick: () => navigate("/ambassador/dashboard"),
        testid: "ambassador-apply-open-dashboard",
        disabled: false,
      };
    }
    return null;
  }, [isLoggedIn, status, navigate]);

  const submit = async () => {
    if (submitting || pitchTooShort) {
      toast.error("Tell us at least 20 characters about why you'd be a great Ambassador.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        pitch: pitch.trim(),
        instagram: socials.instagram.trim() || null,
        tiktok:    socials.tiktok.trim()    || null,
        x:         socials.x.trim()         || null,
        youtube:   socials.youtube.trim()   || null,
        follower_count: followerCount ? Math.max(0, parseInt(followerCount, 10) || 0) : null,
      };
      await api.post("/ambassador/apply", payload);
      toast.success("Application in — we'll ping you when it's reviewed.");
      // Refresh status
      const { data } = await api.get("/ambassador/me");
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="ambassador-apply-page">
      <div className="max-w-3xl mx-auto px-4 pt-6">
        {/* Back nav */}
        <Link
          to="/ambassadors"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"
          data-testid="ambassador-apply-back"
        >
          <ChevronLeft size={14} /> Leaderboard
        </Link>

        {/* Hero */}
        <div className="text-center pt-4 pb-10 border-b border-white/10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-amber-400">
            <Sparkles size={14} /> Panda Ambassador Program
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">
            Wear the gold.
            <br />
            Grow the pack.
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base mt-5 max-w-xl mx-auto leading-relaxed">
            Panda Ambassadors are the loud, loyal creators keeping this place alive. Apply once — get
            a badge, monthly coin drops, and boosted reach for as long as you're active.
          </p>

          {/* Status pill for signed-in users */}
          {isLoggedIn && !loadingState && status === "applied" && (
            <div
              className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-widest text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-2"
              data-testid="ambassador-apply-status-applied"
            >
              <Clock size={12} /> Your application is under review
            </div>
          )}
          {isLoggedIn && !loadingState && status === "rejected" && (
            <div
              className="inline-flex flex-col items-center gap-2 mt-6"
              data-testid="ambassador-apply-status-rejected"
            >
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-full px-4 py-2">
                <XCircle size={12} /> Application not approved this round
              </div>
              {state?.reject_reason && (
                <p className="text-xs text-zinc-400 max-w-md text-center leading-relaxed">
                  <b className="text-zinc-300">Feedback from the team:</b> {state.reject_reason}
                </p>
              )}
            </div>
          )}
          {isLoggedIn && !loadingState && status === "approved" && (
            <div
              className="flex flex-col items-center gap-3 mt-6"
              data-testid="ambassador-apply-status-approved"
            >
              <AmbassadorBadge status="approved" tier={state?.tier} size="lg" linkTo={null} />
              <Link
                to="/ambassador/dashboard"
                data-testid="ambassador-apply-dashboard-link"
                className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 underline underline-offset-2"
              >
                <LayoutDashboard size={12} /> Open your Ambassador dashboard
              </Link>
            </div>
          )}
        </div>

        {/* Perks grid */}
        <section className="py-10">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-4">The perks</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {PERKS.map((p) => (
              <div
                key={p.title}
                className="bg-zinc-950/60 border border-white/10 rounded-md p-4 hover:border-amber-500/40 transition-colors"
                data-testid={`ambassador-perk-${p.title.split(" ")[0].toLowerCase()}`}
              >
                <div className="flex items-center gap-2 text-amber-300 mb-2">
                  <p.icon size={16} />
                  <div className="text-sm font-semibold text-white">{p.title}</div>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-600 mt-3">
            Monthly drop currently set at{" "}
            <b className="text-amber-300">{monthlyCoins.toLocaleString()} coins</b> per approved
            Ambassador. Amount can be adjusted by the team over time.
          </p>
        </section>

        {/* Commission schedule — Iter52 */}
        <section className="py-10 border-t border-white/10" data-testid="ambassador-commission-schedule">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-4">
            Referral commissions · how they work
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed mb-5">
            When someone signs up through your referral link and later becomes a creator, you earn a
            share of the tips + creator-shop sales they receive — <b>in real USD, paid via Stripe</b>.
            The clock starts on their <b>first successful paid transaction</b> (not signup) and slows
            over time so you're rewarded most for bringing in fresh performers.
          </p>
          <div className="bg-black/40 border border-white/10 rounded-md p-4" data-testid="ambassador-commission-note">
            <p className="text-xs text-zinc-400 leading-relaxed">
              Commission rates are set by the My Panda X team and shared with approved Ambassadors in their
              dashboard. Rates step down over each referred creator&apos;s first months and may be adjusted
              over time. The platform&apos;s share is never reduced — commissions come only out of the
              creator&apos;s portion, and sub-penny remainders always go to the creator.
            </p>
          </div>
          <ul className="text-xs text-zinc-400 leading-relaxed mt-4 space-y-1.5 list-disc pl-5">
            <li>Only <b>fan tips</b> and <b>creator-shop purchases</b> qualify. Coin gifts and live gifts don't count.</li>
            <li>Refunds & chargebacks reverse the matching commission automatically.</li>
            <li>Earnings sit in your dashboard for <b>30 days</b> (a dispute-safe holding window) before they become withdrawable.</li>
            <li>Manual monthly payout via Stripe Connect, <b>$200 minimum</b> (the same floor as a Bamboo cash-out: 80,000 🎋 = $200). You'll need to connect your Stripe account before your first withdrawal.</li>
            <li>Attribution is <b>one-shot at signup</b> and never re-assigned — the person who invited them is their ambassador for life.</li>
          </ul>
        </section>

        {/* How to apply — steps */}
        <section className="py-10 border-t border-white/10">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-4">How to apply</div>
          <div className="space-y-3">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="flex gap-4 bg-zinc-950/40 border border-white/5 rounded-md p-4"
                data-testid={`ambassador-step-${s.n}`}
              >
                <div className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 text-black font-display text-sm flex items-center justify-center">
                  {s.n}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{s.title}</div>
                  <p className="text-xs text-zinc-400 leading-relaxed mt-1">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Requirements */}
        <section className="py-10 border-t border-white/10">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-4">Requirements</div>
          <ul className="space-y-2">
            {REQUIREMENTS.map((r) => (
              <li key={r} className="flex items-start gap-2 text-sm text-zinc-300">
                <ShieldCheck size={14} className="text-emerald-400 mt-0.5 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Application form / CTA */}
        <section
          className="py-10 border-t border-white/10"
          data-testid="ambassador-apply-form-section"
        >
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-4">
            Your application
          </div>

          {loadingState || authChecking ? (
            <div className="flex items-center justify-center py-8 text-zinc-500">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading your status…
            </div>
          ) : primaryCta ? (
            // Not signed in OR already approved — big single CTA
            <button
              type="button"
              onClick={primaryCta.onClick}
              disabled={primaryCta.disabled}
              data-testid={primaryCta.testid}
              className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-full py-3 text-sm flex items-center justify-center gap-2"
            >
              {primaryCta.label}
              <ArrowRight size={14} />
            </button>
          ) : (
            <div className="space-y-4">
              {status === "applied" && (
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Your pitch is already in front of the team. You can still tweak it below and save —
                  the latest version is what we'll see.
                </p>
              )}
              {status === "revoked" && (
                <p className="text-sm text-rose-300 leading-relaxed">
                  Your previous Ambassador status was revoked. You're welcome to re-apply — a fresh
                  pitch helps.
                </p>
              )}
              {status === "rejected" && (
                <p className="text-sm text-rose-300 leading-relaxed">
                  We weren't able to approve this round. Update your pitch or add social proof
                  below and resubmit — the team will review again.
                </p>
              )}
              {status === "none" && (
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Write a short pitch below (20 – 1000 characters). Be specific — your community,
                  your niche, why you'd repping Panda X matters.
                </p>
              )}
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">
                  Your pitch
                </label>
                <textarea
                  data-testid="ambassador-apply-pitch-input"
                  value={pitch}
                  onChange={(e) => setPitch(e.target.value.slice(0, 1000))}
                  placeholder="I'd be an amazing Panda Ambassador because…"
                  rows={6}
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-3 text-white text-sm focus:border-amber-400 outline-none resize-none"
                />
                <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
                  <span>Min 20 characters</span>
                  <span data-testid="ambassador-apply-pitch-count">{pitch.length}/1000</span>
                </div>
              </div>

              {/* Social proof */}
              <div className="border-t border-white/5 pt-4 space-y-3">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                  Social proof <span className="normal-case tracking-normal text-zinc-600">— optional but strongly encouraged</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <SocialInput
                    icon={Instagram}
                    label="Instagram"
                    testid="ambassador-apply-instagram"
                    prefix="@"
                    value={socials.instagram}
                    onChange={(v) => setSocials((s) => ({ ...s, instagram: v }))}
                  />
                  <SocialInput
                    icon={Sparkles}
                    label="TikTok"
                    testid="ambassador-apply-tiktok"
                    prefix="@"
                    value={socials.tiktok}
                    onChange={(v) => setSocials((s) => ({ ...s, tiktok: v }))}
                  />
                  <SocialInput
                    icon={Sparkles}
                    label="X (Twitter)"
                    testid="ambassador-apply-x"
                    prefix="@"
                    value={socials.x}
                    onChange={(v) => setSocials((s) => ({ ...s, x: v }))}
                  />
                  <SocialInput
                    icon={Youtube}
                    label="YouTube"
                    testid="ambassador-apply-youtube"
                    prefix="@"
                    value={socials.youtube}
                    onChange={(v) => setSocials((s) => ({ ...s, youtube: v }))}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block">
                    Total following across platforms
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    data-testid="ambassador-apply-follower-count"
                    value={followerCount}
                    onChange={(e) => setFollowerCount(e.target.value.replace(/[^0-9]/g, "").slice(0, 12))}
                    placeholder="e.g. 15000"
                    className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:border-amber-400 outline-none"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={submitting || pitchTooShort}
                data-testid="ambassador-apply-submit"
                className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-full py-3 text-sm flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    {status === "applied"
                      ? "Update my application"
                      : status === "revoked" || status === "rejected"
                      ? "Re-apply"
                      : "Submit application"}
                  </>
                )}
              </button>
              {status === "applied" && (
                <p className="text-[11px] text-zinc-600 text-center">
                  We'll notify you the moment a decision is made.
                </p>
              )}
            </div>
          )}
        </section>

        {/* Community footer */}
        <section className="pt-8 pb-4 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-3">
            <Users size={12} /> Ambassadors currently on the leaderboard
          </div>
          <div>
            <Link
              to="/ambassadors"
              className="inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200"
              data-testid="ambassador-apply-view-leaderboard"
            >
              <Trophy size={14} /> See the current pack <ArrowRight size={12} />
            </Link>
          </div>
          <p className="text-[11px] text-zinc-600 mt-6 flex items-center justify-center gap-1">
            <Heart size={10} className="text-rose-500" /> Built with the community, for the community.
          </p>
        </section>
      </div>
    </div>
  );
}

function SocialInput({ icon: Icon, label, prefix, testid, value, onChange }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 block flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </label>
      <div className="flex items-center bg-black/40 border border-white/10 rounded-md focus-within:border-amber-400 transition">
        {prefix && (
          <span className="pl-3 text-zinc-500 text-sm select-none">{prefix}</span>
        )}
        <input
          type="text"
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, 200))}
          placeholder="handle or url"
          className="w-full bg-transparent px-2 py-2.5 text-white text-sm outline-none"
        />
      </div>
    </div>
  );
}
